// AI-CONTEXT: See docs/features/updates-mailing.md
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'
import firebase from '../../firebase/compat'
import PageHeader from '../../components/layout/PageHeader'
import Modal from '../../components/common/Modal'
import useUpdatesMailingPreview from '../../hooks/useUpdatesMailingPreview'
import { client } from '../../hooks/Client'

/**
 * Waar het scherm mee opent. De gebruiker past het aan; dit is alleen een start.
 * `subject` geldt voor elk merk tot er per merk iets anders is getypt; de
 * merken zelf zijn pas bekend als het voorbeeld binnen is.
 */
const START = {
    subject: 'Er is weer een hoop verbeterd',
    intro: 'Hoi,\n\nEr is de afgelopen tijd veel aan de software gewerkt. '
        + 'Op de pagina Updates lees je in gewone taal wat er nieuw is voor jouw organisatie.',
    actionLabel: 'Bekijk wat er nieuw is',
}


/**
 * Het verzendscherm van de updatemailing, in de Deccos-beheeromgeving.
 *
 * Drie toestanden in strikte volgorde: nog niet bekend, mislukt, en pas dan de
 * inhoud. Een aantal tonen terwijl de gegevens onderweg zijn is een bewering
 * aan de lezer die niet waar is — en hier is die bewering "er is niemand om te
 * mailen", vlak voordat iemand op verzenden drukt.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * Mislukt een bijwerking terwijl er al inhoud staat, dan blijft die inhoud
 * staan en komt de fout erboven. Anders verdwijnt alles wat er getypt is
 * achter één foutregel, zonder weg terug behalve de pagina herladen.
 *
 * Het voorbeeld komt van de server, niet uit een tweede sjabloon hier. Zou dit
 * scherm zijn eigen HTML bouwen, dan controleert de testknop iets anders dan
 * wat de ontvanger krijgt.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 */
const UpdatesMailing = () => {

    // State
    // Onderwerp per merk-id; een merk dat hier ontbreekt houdt START.subject.
    // Een FIXtool-klant hoort in de onderwerpregel geen 'Deccos' te lezen.
    const [subjects, setSubjects] = useState(/** @type {Object<string, string>} */ ({}))
    const [intro, setIntro] = useState(START.intro)
    const [merk, setMerk] = useState('deccos')
    const [bezig, setBezig] = useState('')
    const [melding, setMelding] = useState(/** @type {{soort: string, tekst: string} | null} */ (null))
    const [bevestigOpen, setBevestigOpen] = useState(false)

    // Het verzend-id is de hele bescherming tegen dubbel versturen: de server
    // maakt er zijn logboekdocument mee aan en leidt er de document-id van elke
    // mail uit af. Het moet dus BLIJVEN staan als een poging misgaat.
    //
    // De aanroep valt in de browser standaard na 70 seconden terug met een fout,
    // terwijl de functie zelf 540 seconden mag draaien. Een trage verzending
    // toont dus een foutmelding terwijl hij server-side gewoon slaagt. Werd hier
    // elke klik een nieuw id, dan zou de tweede poging een nieuwe reeks
    // document-id's opleveren en kreeg iedereen de mail twee keer -- en dat is
    // niet terug te halen.
    const verzendId = useRef(uuid())

    // Hooks
    const { data, loading, error, refresh, markBrandResolved } = useUpdatesMailingPreview(START)

    // De merken komen van de server en staan niet als tweede lijst in dit
    // bestand. brands.js zegt dat een derde whitelabel daar en nergens anders
    // hoort; een kopie hier zou dat stilletjes onwaar maken -- de tellingen en
    // het voorbeeld zouden er dan eentje missen terwijl het totaal wél klopt.
    // AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
    const merken = Object.keys(data?.previews || {})
        .map((id) => ({ id, label: data.previews[id].brandName || id }))

    /** @param {string} id - merk-id */
    const onderwerpVan = (id) => subjects[id] ?? START.subject

    const concept = {
        subjects: Object.fromEntries(merken.map(({ id }) => [id, onderwerpVan(id)])),
        intro,
        actionLabel: START.actionLabel,
    }

    /**
     * @param {string} id - merk-id
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    const wijzigOnderwerp = (id, e) => {
        // Eerst uitlezen, dan pas de updater: e.target is het levende element en
        // React heeft er de oude waarde alweer in teruggezet tegen de tijd dat
        // de updater draait.
        // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
        const value = e.target.value
        setSubjects((prev) => ({ ...prev, [id]: value }))
    }
    const zonderMerk = data?.unknownBrandTenants || []
    const magVerzenden = !!data && zonderMerk.length === 0 && (data.totalRecipients || 0) > 0

    /**
     * @param {string} naam - naam van de callable
     * @param {object} payload
     * @param {string} bezigLabel
     * @param {(uitkomst: object) => string} gelukt
     */
    const roepAan = async (naam, payload, bezigLabel, gelukt) => {
        setBezig(bezigLabel)
        setMelding(null)
        try {
            // Zonder deze grens valt de aanroep na 70 seconden terug met een
            // fout terwijl de functie nog draait (die mag 540 seconden), en
            // leest een geslaagde verzending als een mislukte.
            const fn = firebase.app().functions('europe-west1')
                .httpsCallable(naam, { timeout: 540000 })
            const result = await fn(payload)
            setMelding({ soort: 'ok', tekst: gelukt(result.data || {}) })
            return result.data
        } catch (err) {
            console.error(`${naam} error:`, err)
            // De melding komt van de server: die kent de regels, dit scherm niet.
            // Wel met een zin eromheen: bij een functie die niet bestaat of niet
            // bereikbaar is geeft Firebase alleen de code terug, en dan staat er
            // letterlijk 'internal' op het scherm.
            setMelding({ soort: 'error', tekst: `Er ging iets mis: ${err.message || 'onbekende fout'}.` })
            return null
        } finally {
            setBezig('')
        }
    }

    const kiesMerk = async (docId, brand) => {
        const uitkomst = await roepAan(
            'setCompagnyBrand', { docId, brand }, `merk-${docId}`,
            () => 'Merk opgeslagen.'
        )
        if (!uitkomst) return

        markBrandResolved(docId)

        // De aantallen kloppen nu niet meer: de gebruikers van deze organisatie
        // stonden geboekt als 'onbekend merk' en tellen voortaan mee. Zonder
        // deze verversing zegt de bevestiging straks een lager getal dan er
        // werkelijk verstuurd wordt. De getypte tekst overleeft het, want die
        // gaat als argument mee.
        refresh(concept)
    }

    // De organisatie van waaruit je verstuurt gaat mee: de knop in de testmail
    // wijst daarheen, zodat hij bij de tester landt en niet bij een willekeurige
    // klant waar hij geen lid van is.
    const stuurTest = () => roepAan(
        'sendUpdatesMailingTest', { ...concept, compagnyId: client }, 'test',
        (d) => `Testmail verstuurd naar ${d.naar} (${d.verstuurd} berichten, één per merk).`
    )

    const verstuur = async () => {
        setBevestigOpen(false)
        const uitkomst = await roepAan(
            'sendUpdatesMailing', { ...concept, mailingId: verzendId.current }, 'verzenden',
            (d) => `Verstuurd naar ${d.verzonden} ontvangers`
                + (d.overgeslagen ? `, ${d.overgeslagen} overgeslagen` : '')
                + (d.mislukt?.length ? `, ${d.mislukt.length} mislukt` : '')
                + '.'
        )
        // Pas ná een geslaagde verzending een nieuw id: de volgende mailing is
        // een andere mailing. Mislukte het, dan houden we hetzelfde id, zodat
        // een tweede poging de al verstuurde mails overslaat in plaats van ze
        // te herhalen.
        if (uitkomst) verzendId.current = uuid()
        refresh(concept)
    }

    // JSX
    return (
        <div className='updates-mailing'>
            <PageHeader heading='Updates mailen' />

            {loading && <p className='updates-mailing-state'>Bezig met laden…</p>}

            {!loading && error && (
                <p className='updates-mailing-state updates-mailing-state--error'>{error}</p>
            )}

            {!loading && data && (
                <div className='updates-mailing-body'>

                    <div className='updates-mailing-column'>

                        {merken.map(({ id, label }) => (
                            <div className='updates-mailing-field' key={id}>
                                <label htmlFor={`mailing-subject-${id}`}>{`Onderwerp (${label})`}</label>
                                <input
                                    id={`mailing-subject-${id}`}
                                    type='text'
                                    value={onderwerpVan(id)}
                                    onChange={(e) => wijzigOnderwerp(id, e)}
                                />
                            </div>
                        ))}

                        <label htmlFor='mailing-intro'>Introtekst</label>
                        <textarea
                            id='mailing-intro'
                            rows={7}
                            value={intro}
                            onChange={(e) => setIntro(e.target.value)}
                        />

                        <button
                            type='button'
                            className='updates-mailing-secondary'
                            onClick={() => refresh(concept)}
                        >
                            Voorbeeld bijwerken
                        </button>

                        <h2 className='updates-mailing-heading'>Wie krijgt hem</h2>
                        <p className='updates-mailing-count'>
                            {`${data.totalRecipients} ontvangers`}
                        </p>
                        <ul className='updates-mailing-list'>
                            {merken.map(({ id, label }) => (
                                <li key={id}>{`${label}: ${data.perBrand?.[id] ?? 0}`}</li>
                            ))}
                        </ul>

                        {zonderMerk.length > 0 && (
                            <div className='updates-mailing-blocker'>
                                <h2 className='updates-mailing-heading'>
                                    {`${zonderMerk.length} organisatie(s) hebben nog geen merk`}
                                </h2>
                                <p>
                                    Zolang dit openstaat kan er niets verstuurd worden: hun
                                    gebruikers zouden anders stil de verkeerde huisstijl krijgen.
                                </p>
                                {zonderMerk.map((org) => (
                                    <div className='updates-mailing-brand-row' key={org.docId}>
                                        <span>{org.name || org.compagnyId}</span>
                                        {merken.map(({ id, label }) => (
                                            <button
                                                key={id}
                                                type='button'
                                                disabled={bezig === `merk-${org.docId}`}
                                                onClick={() => kiesMerk(org.docId, id)}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className='updates-mailing-actions'>
                            <button
                                type='button'
                                className='updates-mailing-secondary'
                                disabled={!!bezig}
                                onClick={stuurTest}
                            >
                                {bezig === 'test' ? 'Bezig…' : 'Testmail naar info@deccos.nl'}
                            </button>
                            <button
                                type='button'
                                disabled={!magVerzenden || !!bezig}
                                onClick={() => setBevestigOpen(true)}
                            >
                                {bezig === 'verzenden' ? 'Bezig…' : 'Versturen'}
                            </button>
                        </div>

                        {melding && (
                            <p className={`updates-mailing-state updates-mailing-state--${melding.soort === 'ok' ? 'ok' : 'error'}`}>
                                {melding.tekst}
                            </p>
                        )}

                    </div>

                    <div className='updates-mailing-column'>
                        <h2 className='updates-mailing-heading'>Zo ziet hij eruit</h2>
                        <div className='updates-mailing-tabs'>
                            {merken.map(({ id, label }) => (
                                <button
                                    key={id}
                                    type='button'
                                    className={id === merk ? 'is-active' : ''}
                                    onClick={() => setMerk(id)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        {/* In een iframe: de mail brengt zijn eigen opmaak mee,
                            en die hoort niet met de pagina te vermengen. */}
                        <iframe
                            className='updates-mailing-preview'
                            title={`Voorbeeld ${merk}`}
                            srcDoc={data.previews?.[merk]?.html || ''}
                        />
                    </div>

                </div>
            )}

            <Modal
                modalOpen={bevestigOpen}
                setModalOpen={setBevestigOpen}
                confirm={verstuur}
                confirmLabel='Ja, versturen'
                pageHeader='De mailing versturen'
                content={
                    <p>
                        {`Er gaan ${data?.totalRecipients ?? 0} mails de deur uit: `}
                        {merken.map(({ id, label }) => `${data?.perBrand?.[id] ?? 0} × ${label}`).join(', ')}
                        {'. Dit is niet terug te draaien.'}
                    </p>
                }
            />

        </div>
    )
}

export default UpdatesMailing
